/**
 * Menu Builder - Generates menu structure from route configuration
 * 
 * This service builds the menu structure from route definitions,
 * ensuring consistency between routes and menu items.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { routeConfig } from '../../config/routes.config';
import { checkMenuPermissions, PERMISSION_MAPPING, checkModuleAccess, getModulesForMenuItem, checkFeatureAccess } from './permissionMapping';
import { renderGroupLabel } from './index';
import IconImage from '../../components/common/icon/IconImage';
import { Typography } from 'antd';
import { BsFillExclamationTriangleFill } from "react-icons/bs";
import {
  PiCalendarBlankDuotone,
  PiIdentificationCardDuotone,
  PiNotebookDuotone,
  PiCreditCardDuotone,
  PiPackageDuotone,
  PiShoppingBagDuotone,
  PiScalesDuotone,
  PiBuildingsDuotone,
  PiStorefrontDuotone,
  PiGearFineDuotone,
  PiChartBarDuotone,
  PiHeadsetDuotone,
  PiBankDuotone,
  PiListChecksDuotone,
  PiSquaresFourDuotone,
  // Add more icons as needed
} from "react-icons/pi";
import VarexeLogo from '../../components/common/VarexeLogo';
import { GoTasklist } from "react-icons/go"
const { Text } = Typography;

/**
 * Icon mapping for menu items
 */
const iconMap = {
  dashboard: <PiSquaresFourDuotone className='fs-2'  />,
  calendar: <PiCalendarBlankDuotone className='fs-2 text-success' />,
  profile: <PiIdentificationCardDuotone className='fs-2' />,
  todos: <GoTasklist className='fs-2' />,
  sales: <PiCreditCardDuotone className='fs-2' />,
  services: <PiPackageDuotone className='fs-2' />,
  settings: <PiGearFineDuotone className='fs-2' />,
  reports: <PiChartBarDuotone className='fs-2' />,
  support: <PiHeadsetDuotone className='fs-1' />,
  licenses: <VarexeLogo type={'svgIcon'} style={{width:'20px'}} className='fs-1' />,
  businessprofile: <PiStorefrontDuotone className='fs-2' />,
  // Add more icon mappings
};

/**
 * Get menu label (handles both string and function)
 */
const getMenuLabel = (menuConfig, authContext) => {
  if (typeof menuConfig.label === 'function') {
    return menuConfig.label(authContext);
  }
  return menuConfig.label || '';
};

/**
 * Build menu item from route config
 */
const buildMenuItem = (route, userPermissions, moduleAccess, licenseFeatures, authContext) => {
  const { menu, permissions = [], modules = [], features = [] } = route;

  if (!menu || menu.hide) {
    return null;
  }

  // Check license feature access (e.g. items_advanced for configurable templates/groups)
  if (features.length > 0) {
    if (!checkFeatureAccess(features, licenseFeatures)) {
      return null;
    }
  }

  // Check permissions - use route permissions or fallback to PERMISSION_MAPPING
  let requiredPermissions = permissions;
  if (requiredPermissions.length === 0) {
    // For nested items, check parent mapping first (e.g., sales.invoices)
    if (menu.parent) {
      const parentMapping = PERMISSION_MAPPING[menu.parent];
      if (parentMapping && typeof parentMapping === 'object' && parentMapping[menu.key]) {
        requiredPermissions = parentMapping[menu.key];
      }
    }
    // If still no permissions, try direct mapping
    if (requiredPermissions.length === 0) {
      const mapping = PERMISSION_MAPPING[menu.key];
      if (mapping) {
        requiredPermissions = Array.isArray(mapping) ? mapping : [];
      }
    }
  }
  
  const { hasAccess, isLimited } = checkMenuPermissions(requiredPermissions, userPermissions);

  if (!hasAccess) {
    return null;
  }

  // Check module access
  if (modules.length > 0) {
    const hasModuleAccess = checkModuleAccess(modules, moduleAccess);
    if (!hasModuleAccess) {
      return null;
    }
  }

  const label = getMenuLabel(menu, authContext);
  const icon = typeof menu.icon === 'string' ? iconMap[menu.icon] : menu.icon;

  return {
    key: menu.key,
    label: (
      <div className="d-flex justify-content-between align-items-center w-100">
        <Link className='text-decoration-none' to={route.path}>
          {isLimited && (
            <Text type='warning' className='fs--1 me-1'>
              <BsFillExclamationTriangleFill />
            </Text>
          )}
          {label}
        </Link>
      </div>
    ),
    icon: icon,
    path: route.path,
    // Note: matchPattern removed to avoid React DOM warnings
    // Path matching uses 'path' property directly
  };
};

/**
 * Build menu structure from routes
 */
export const buildMenuFromRoutes = (userPermissions = [], authContext = null) => {
  const moduleAccess = authContext?.user?.extra?.privileges?.module_access || [];
  const licenseFeatures = authContext?.user?.extra?.privileges?.license_features || [];
  
  // Group routes by parent menu
  const menuMap = new Map();
  const rootItems = [];

  // First pass: build all menu items
  routeConfig.forEach(route => {
    const menuItem = buildMenuItem(route, userPermissions, moduleAccess, licenseFeatures, authContext);
    if (!menuItem) return;

    if (route.menu.parent) {
      // Add to parent group
      if (!menuMap.has(route.menu.parent)) {
        menuMap.set(route.menu.parent, []);
      }
      menuMap.get(route.menu.parent).push(menuItem);
    } else {
      // Root level item
      rootItems.push(menuItem);
    }
  });

  // Second pass: build parent menus with children
  const parentMenus = {
    sales: {
      key: 'sales',
      label: (authContext) => authContext?.settings?.sales_menu_title || 'Sales',
      icon: <PiCreditCardDuotone className='fs-2' />,
      children: menuMap.get('sales') || [],
      permissions: PERMISSION_MAPPING.sales?.group || [],
    },
    servicesproducts: {
      key: 'servicesproducts',
      label: (authContext) => authContext?.settings?.services_menu_title || 'Services & Products',
      icon: <PiPackageDuotone className='fs-2' />,
      children: menuMap.get('servicesproducts') || [],
      permissions: PERMISSION_MAPPING.services?.group || [],
    },
    inventory: {
      key: 'inventory',
      label: 'Inventory',
      icon: <PiPackageDuotone className='fs-2' />,
      children: menuMap.get('inventory') || [],
      permissions: PERMISSION_MAPPING.inventory?.group || [],
    },
    purchases: {
      key: 'purchases',
      label: 'Purchases',
      icon: <PiShoppingBagDuotone className='fs-2' />,
      children: menuMap.get('purchases') || [],
      permissions: PERMISSION_MAPPING.purchases?.group || [],
    },
    accounting: {
      key: 'accounting',
      label: (authContext) => authContext?.settings?.accounting_menu_title || 'Accounting',
      icon: <PiScalesDuotone className='fs-2' />,
      children: menuMap.get('accounting') || [],
      permissions: PERMISSION_MAPPING.accounting?.group || [],
    },
    personal: {
      key: 'personal',
      label: 'Personal',
      children: menuMap.get('personal') || [],
      permissions: [],
    },
    myOrganization: {
      key: 'myOrganization',
      label: 'My Organization',
      icon: <PiBuildingsDuotone className='fs-2' />,
      children: menuMap.get('myOrganization') || [],
      permissions: PERMISSION_MAPPING.organization?.group || [],
    },
    businessprofile: {
      key: 'businessprofile',
      label: 'Business profile',
      icon: <PiStorefrontDuotone className='fs-2' />,
      children: menuMap.get('businessprofile') || [],
      // Business profile has its own permission group so an editor can be
      // granted view here without inheriting other org-admin access.
      permissions: PERMISSION_MAPPING.business_profile?.view || [],
    },
  };

  // Build final menu structure following the same order as old system
  const finalMenu = [];

  // Core Navigation - Always at top
  const dashboardItem = rootItems.find(item => item.key === 'dashboard');
  if (dashboardItem) finalMenu.push(dashboardItem);
  
  const calendarItem = rootItems.find(item => item.key === 'calendar');
  if (calendarItem && checkModuleAccess(['calendar'], moduleAccess)) {
    finalMenu.push(calendarItem);
  }

  // Primary Business Operations - Sales & Services together
  const salesGroupChildren = [];
  if (parentMenus.sales.children.length > 0) {
    const { hasAccess } = checkMenuPermissions(parentMenus.sales.permissions, userPermissions);
    if (hasAccess) {
      salesGroupChildren.push({
        key: parentMenus.sales.key,
        label: typeof parentMenus.sales.label === 'function' ? parentMenus.sales.label(authContext) : parentMenus.sales.label,
        icon: parentMenus.sales.icon,
        children: parentMenus.sales.children.sort((a, b) => {
          const routeA = routeConfig.find(r => r.menu?.key === a.key);
          const routeB = routeConfig.find(r => r.menu?.key === b.key);
          return (routeA?.menu?.order || 999) - (routeB?.menu?.order || 999);
        }),
      });
    }
  }
  if (parentMenus.servicesproducts.children.length > 0) {
    const { hasAccess } = checkMenuPermissions(parentMenus.servicesproducts.permissions, userPermissions);
    if (hasAccess) {
      salesGroupChildren.push({
        key: parentMenus.servicesproducts.key,
        label: typeof parentMenus.servicesproducts.label === 'function' ? parentMenus.servicesproducts.label(authContext) : parentMenus.servicesproducts.label,
        icon: parentMenus.servicesproducts.icon,
        children: parentMenus.servicesproducts.children.sort((a, b) => {
          const routeA = routeConfig.find(r => r.menu?.key === a.key);
          const routeB = routeConfig.find(r => r.menu?.key === b.key);
          return (routeA?.menu?.order || 999) - (routeB?.menu?.order || 999);
        }),
      });
    }
  }
  if (salesGroupChildren.length > 0) {
    finalMenu.push({
      type: 'group',
      label: renderGroupLabel('Sales & Services', PERMISSION_MAPPING.sales?.group || [], userPermissions),
      key: 'SalesGroup',
      children: salesGroupChildren,
    });
  }

  // Inventory
  if (parentMenus.inventory.children.length > 0) {
    const { hasAccess } = checkMenuPermissions(parentMenus.inventory.permissions, userPermissions);
    if (hasAccess) {
      finalMenu.push({
        type: 'group',
        label: renderGroupLabel('Inventory', PERMISSION_MAPPING.inventory?.group || [], userPermissions),
        key: 'inventoryGroup',
        children: [{
          key: parentMenus.inventory.key,
          label: parentMenus.inventory.label,
          icon: parentMenus.inventory.icon,
          children: parentMenus.inventory.children.sort((a, b) => {
            const routeA = routeConfig.find(r => r.menu?.key === a.key);
            const routeB = routeConfig.find(r => r.menu?.key === b.key);
            return (routeA?.menu?.order || 999) - (routeB?.menu?.order || 999);
          }),
        }],
      });
    }
  }

  // Purchases
  if (parentMenus.purchases.children.length > 0) {
    const { hasAccess } = checkMenuPermissions(parentMenus.purchases.permissions, userPermissions);
    if (hasAccess) {
      finalMenu.push({
        type: 'group',
        label: renderGroupLabel('Purchases', PERMISSION_MAPPING.purchases?.group || [], userPermissions),
        key: 'purchasesGroup',
        children: [{
          key: parentMenus.purchases.key,
          label: parentMenus.purchases.label,
          icon: parentMenus.purchases.icon,
          children: parentMenus.purchases.children.sort((a, b) => {
            const routeA = routeConfig.find(r => r.menu?.key === a.key);
            const routeB = routeConfig.find(r => r.menu?.key === b.key);
            return (routeA?.menu?.order || 999) - (routeB?.menu?.order || 999);
          }),
        }],
      });
    }
  }

  // Financial Management
  if (parentMenus.accounting.children.length > 0) {
    const { hasAccess } = checkMenuPermissions(parentMenus.accounting.permissions, userPermissions);
    if (hasAccess) {
      finalMenu.push({
        type: 'group',
        label: renderGroupLabel('Financial', PERMISSION_MAPPING.accounting?.group || [], userPermissions),
        key: 'financialGroup',
        children: [{
          key: parentMenus.accounting.key,
          label: typeof parentMenus.accounting.label === 'function' ? parentMenus.accounting.label(authContext) : parentMenus.accounting.label,
          icon: parentMenus.accounting.icon,
          children: parentMenus.accounting.children.sort((a, b) => {
            const routeA = routeConfig.find(r => r.menu?.key === a.key);
            const routeB = routeConfig.find(r => r.menu?.key === b.key);
            return (routeA?.menu?.order || 999) - (routeB?.menu?.order || 999);
          }),
        }],
      });
    }
  }

  // Bank Accounts (standalone)
  const bankAccountItem = rootItems.find(item => item.key === 'bankaccounts');
  if (bankAccountItem && checkModuleAccess(['bank_accounts'], moduleAccess)) {
    finalMenu.push(bankAccountItem);
  }

  // Reports
  const reportsItem = rootItems.find(item => item.key === 'reports');
  if (reportsItem && checkModuleAccess(['reports'], moduleAccess)) {
    finalMenu.push({
      type: 'group',
      label: renderGroupLabel('Reports', PERMISSION_MAPPING.reports || [], userPermissions),
      key: 'reportsGroup',
    });
    finalMenu.push(reportsItem);
  }

  // Personal Tools
  if (parentMenus.personal.children.length > 0) {
    finalMenu.push({
      type: 'group',
      label: renderGroupLabel('Personal', [], userPermissions),
      key: 'personalGroup',
    });
    parentMenus.personal.children
      .sort((a, b) => {
        const routeA = routeConfig.find(r => r.menu?.key === a.key);
        const routeB = routeConfig.find(r => r.menu?.key === b.key);
        return (routeA?.menu?.order || 999) - (routeB?.menu?.order || 999);
      })
      .forEach(item => finalMenu.push(item));
  }

  // Organization Management — Business profile (3-child dropdown) rides at
  // the top of the group, then the My Organization dropdown for the rest
  // (Users, Bank Accounts).
  const orgGroupChildren = [];

  if (parentMenus.businessprofile.children.length > 0) {
    const { hasAccess } = checkMenuPermissions(parentMenus.businessprofile.permissions, userPermissions);
    if (hasAccess) {
      orgGroupChildren.push({
        key: parentMenus.businessprofile.key,
        label: parentMenus.businessprofile.label,
        icon: parentMenus.businessprofile.icon,
        children: parentMenus.businessprofile.children.sort((a, b) => {
          const routeA = routeConfig.find(r => r.menu?.key === a.key);
          const routeB = routeConfig.find(r => r.menu?.key === b.key);
          return (routeA?.menu?.order || 999) - (routeB?.menu?.order || 999);
        }),
      });
    }
  }

  if (parentMenus.myOrganization.children.length > 0) {
    const { hasAccess } = checkMenuPermissions(parentMenus.myOrganization.permissions, userPermissions);
    if (hasAccess) {
      orgGroupChildren.push({
        key: parentMenus.myOrganization.key,
        label: parentMenus.myOrganization.label,
        icon: parentMenus.myOrganization.icon,
        children: parentMenus.myOrganization.children.sort((a, b) => {
          const routeA = routeConfig.find(r => r.menu?.key === a.key);
          const routeB = routeConfig.find(r => r.menu?.key === b.key);
          return (routeA?.menu?.order || 999) - (routeB?.menu?.order || 999);
        }),
      });
    }
  }

  if (orgGroupChildren.length > 0) {
    finalMenu.push({
      type: 'group',
      label: renderGroupLabel('Organization', PERMISSION_MAPPING.organization?.group || [], userPermissions),
      key: 'OrganizationGroup',
      children: orgGroupChildren,
    });
  }

  // System Administration
  const systemChildren = [];
  const settingsItem = rootItems.find(item => item.key === 'settings');
  if (settingsItem && checkModuleAccess(['settings'], moduleAccess)) {
    systemChildren.push(settingsItem);
  }
  const licensesItem = rootItems.find(item => item.key === 'licenses');
  if (licensesItem && checkModuleAccess(['organization_licenses'], moduleAccess)) {
    systemChildren.push(licensesItem);
  }
  if (systemChildren.length > 0) {
    finalMenu.push({
      type: 'group',
      label: renderGroupLabel('System', PERMISSION_MAPPING.admin?.group || [], userPermissions),
      key: 'systemGroup',
    });
    finalMenu.push(...systemChildren);
  }

  // Support
  const supportItem = rootItems.find(item => item.key === 'support');
  if (supportItem) {
    finalMenu.push({
      type: 'group',
      label: renderGroupLabel('Support', PERMISSION_MAPPING.support || [], userPermissions),
      key: 'supportGroup',
    });
    finalMenu.push(supportItem);
  }

  return finalMenu;
};

/**
 * Normalize path for comparison (no trailing/leading slashes, single slashes)
 */
const normalizePath = (path) => {
  if (!path) return '';
  return '/' + String(path).replace(/^\/+|\/+$/g, '').replace(/\/+/g, '/');
};

/**
 * Get path segments (e.g. '/invoices/list' -> ['invoices', 'list'])
 */
const getPathSegments = (path) => {
  if (!path) return [];
  return normalizePath(path).split('/').filter(Boolean);
};

/**
 * Find menu item key by pathname.
 * Matches: exact path, prefix (path + '/...'), or same section (shared leading path segments).
 */
export const findMenuKeyByPath = (pathname, menuItems) => {
  if (!menuItems || menuItems.length === 0) return null;

  const normalizedPathname = normalizePath(pathname);
  const pathnameSegments = getPathSegments(pathname);

  let bestMatch = null;
  let bestSpecificity = 0;

  const checkItem = (item, parentKeys = []) => {
    if (item.path) {
      const normalizedItemPath = normalizePath(item.path);
      const itemPathSegments = getPathSegments(item.path);

      // 1. Exact match (highest priority)
      if (normalizedPathname === normalizedItemPath) {
        if (100 > bestSpecificity) {
          bestMatch = {
            key: item.key,
            parentKeys: parentKeys,
            specificity: 100,
          };
          bestSpecificity = 100;
        }
      }
      // 2. Prefix match (e.g. /invoices/list matches /invoices/list/123)
      else if (normalizedPathname.startsWith(normalizedItemPath + '/')) {
        const specificity = 80 + Math.min(itemPathSegments.length, 10);
        if (specificity > bestSpecificity) {
          bestMatch = {
            key: item.key,
            parentKeys: parentKeys,
            specificity: specificity,
          };
          bestSpecificity = specificity;
        }
      }
      // 3. Same-section match: shared leading segments (e.g. /invoices/new -> Invoices menu with path /invoices/list)
      else if (pathnameSegments.length > 0 && itemPathSegments.length > 0 && pathnameSegments[0] === itemPathSegments[0]) {
        let matchingSegments = 0;
        for (let i = 0; i < Math.min(pathnameSegments.length, itemPathSegments.length); i++) {
          if (pathnameSegments[i] === itemPathSegments[i]) matchingSegments++;
          else break;
        }
        const specificity = 40 + 10 * matchingSegments;
        if (specificity > bestSpecificity) {
          bestMatch = {
            key: item.key,
            parentKeys: parentKeys,
            specificity: specificity,
          };
          bestSpecificity = specificity;
        }
      }
    }

    if (item.children) {
      item.children.forEach(child => {
        checkItem(child, [...parentKeys, item.key]);
      });
    }
  };

  menuItems.forEach(item => checkItem(item));
  return bestMatch;
};

